import { bulbMedium, femaleQuestionMedium } from '@/assets/icons';

const trs = {
  W5H1_TITLE: `QQDCCP`,
  W5H1_WHAT_EXAMPLES: `¿De qué se trata?\n¿Cuál es el estado de la situación?\n¿Cuáles son sus consecuencias?\n¿Cuáles son los riesgos?`,
  W5H1_WHO_EXAMPLES: `¿A quién afecta?\n¿Quién tiene el problema?\n¿Quién se ve afectado por las consecuencias del problema?\n¿Quién puede verse afectado por su resolución?`,
  W5H1_WHERE_EXAMPLES: `¿Dónde se ha producido el problema?\n¿A qué lugares afecta?\n¿A qué área de producción, a qué máquina?\n¿Cuáles son las condiciones ambientales?`,
  W5H1_WHEN_EXAMPLES: `¿Desde cuándo presenta este problema?\n¿Cuándo aparece?\n¿Cuándo se descubrió el problema?\n¿Cuál es su frecuencia?\n¿Cuándo se produce el riesgo?`,
  W5H1_HOW_EXAMPLES: `¿Cómo se produce el problema?\n¿En que condiciones o circunstancias?\n¿Cómo proceder?\n¿Existen métodos que emplear?\n¿De qué manera emprender su resolución, con qué procedimientos?`,
  W5H1_HOWMUCH_EXAMPLES: `¿Qué cantidad de agua inunda el área de producción?\n¿Cuántos errores ha habido antes de detectar el problema?`,
  W5H1_WHY_EXAMPLES: `¿Por qué es un problema?`,
};

const W5H1 = `
<p>
  La metodología QQDCCP permite tener información elemental pero suficiente sobre todas las dimensiones del problema.
  Con esta herramienta,  cualquier persona puede participar en la identificación completa de todos los aspectos esenciales del tema.
  <br>
  Adopta un enfoque análisis crítico constructivo basado en un cuestionamiento sistemático.
  <br><b>QQDCCCP</b> : ¿<b>Q</b>ué? ¿<b>Q</b>uién? ¿<b>D</b>ónde? ¿<b>C</b>uándo? ¿<b>C</b>ómo? ¿<b>C</b>uánto? ¿<b>P</b>or qué?
</p>
<br>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Estas preguntas son demasiado simples, no se puede describir un problema precisamente de esta manera.</span>
</div>
<br>
<p>
  Cuanto más usamos esta herramienta, más notamos que cada una de estas simples preguntas esconde otras.
  Déjame entrar en más detalles:
</p>
<h2 class="basic-sub-title">
  ¿@:message.WHAT?
</h2>
<p>
  Esta pregunta se hace para una mejor descripción del problema.
  Todas las manifestaciones físicas del problema se pueden describir aquí.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHAT_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.WHO?
</h2>
<p>
  Esta pregunta ayuda a todos a entender a quién afecta el problema.
  La identificación de los diferentes actores hace que la resolución de los problemas sea más eficaz.
  E incluso es necesario ver un poco más allá:
  Y hay que mirar aún más lejos: saber a quién consultar para comprobar la veracidad de las causas o la eficacia de las acciones, por ejemplo, es fundamental.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHO_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.WHERE?
</h2>
<p>
  Esta pregunta se refiere a la descripción de las instalaciones. De hecho, el problema puede
  ocurrir en un lugar por una razón y en otro lugar por otra. No se trata sólo de
  identificar el edificio, sino de identificar todas las características del lugar
  que pueden influir en el problema, como el material del suelo o el tipo de iluminación.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHERE_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.WHEN?
</h2>
<p>
  Hacemos esta pregunta para definir adecuadamente los tiempos. Esto le ayudará
  a relacionar los problemas que ocurren en las mismas circunstancias de tiempo
  o con la misma frecuencia.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHEN_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.HOW?
</h2>
<p>
  Las respuestas a esta pregunta permitirán comprender mejor cómo o dónde encontrar
  y resolver el problema.
  <br>
  Esto debe ser exhaustivo. Si es necesario repetir 10 pasos para que ocurra el problema,
  los 10 pasos deben describirse en detalle. Identificar el procedimiento  para
  reproducir un problema es fundamental para comenzar a determinar sus causas.
  <br>
  Este paso también es una herramienta muy eficaz para eliminar la recurrencia y,
  a menudo, basta con repetir el proceso de reproducción de un problema para encontrar otros.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOW_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.HOW_MUCH?
</h2>
<p>
  Esto permite cuantificar todo sobre el problema. Esta pregunta completa
  el "@:message.HOW" resaltando nociones cuantificables sobre las circunstancias
  de la aparición del problema.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Ejemplos:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOWMUCH_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  ¿@:message.WHY?
</h2>
<p>
  Esta pregunta a veces es delicada. No se debe preguntar "¿Por qué está sucediendo el problema?",
  sino "¿Por qué esta situación es un problema?". Repasemos brevemente nuestro curso sobre
  "@:sensei.SPOT_PB_TITLE" : @:sensei.WHAT_IS_A_PB
  Entonces, ¿cuál es la situación ideal?
  <br/>
</p>
<br/>
<p>
  Recuerde que siempre debe completar la herramienta QQDCCP antes de iniciar el resto de la resolución.
</p>
`;


export default {
  ...trs,
  W5H1,
};
